import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface ProjectCardProps {
  title: string;
  description: string;
  imageUrl: string;
  tags: string[];
  link?: string; // Make link optional
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  imageUrl,
  tags,
  link,
}) => {
  return (
    <motion.div
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="card overflow-hidden group h-[420px] flex flex-col"
    >
      {/* Fixed height image section */}
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all duration-300" />
      </div>
      
      {/* Flexible content section */}
      <div className="p-6 flex flex-col flex-grow">
        {/* Fixed height title - max 2 lines */}
        <h3 className="text-xl font-semibold mb-2 line-clamp-2 min-h-[3.5rem]">{title}</h3>
        
        {/* Fixed height description - exactly 3 lines */}
        <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3 flex-grow text-sm leading-relaxed min-h-[4.5rem]">
          {description}
        </p>
        
        {/* Fixed height tags section */}
        <div className="flex flex-wrap gap-2 mb-4 min-h-[2rem] content-start">
          {tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-md"
            >
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md">
              +{tags.length - 3}
            </span>
          )}
        </div>
        
        {/* Button always at bottom */}
        <div className="mt-auto">
          {link && (
            <Link
              to={link}
              className="block w-full text-center py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-600 dark:border-primary-400 rounded-md hover:bg-primary-600 hover:text-white dark:hover:bg-primary-700 transition-colors duration-300"
            >
              View Project
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard; 